import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Icon,
  Spinner,
  Stack,
  Tag,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { WarningTriangleIcon } from '@tonic-one/react-icons';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogApi } from '@Apis';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import { useLogReducerContext } from '../context';
import { useMisdirectedDetailStore } from './store';
import { renderDetectionReason } from './utils';

interface MisdirectedRound {
  mdDetectRound?: number;
  mdDetectId?: string;
  mdDetectParameters?: Record<string, string>;
  mdDetectStatus?: string;
  mdSentTime?: string;
  msgSubmitTime?: string;
  msgDeliveryTime?: string;
  msgSubject?: string;
  msgTo?: string[] | string;
  msgFrom?: string[] | string;
  mdFileName?: string | string[] | null;
  action?: string;
}

// Final-action coloring per Figma:
// SendAnyway → red, ActionTaken → green, OnHold → orange.
const FINAL_ACTION_COLOR: Record<string, string> = {
  SendAnyway: '#FE4648',
  ActionTaken: '#7FD78D',
  OnHold: '#FEB073',
};

// Action-tag coloring per Figma legend:
// Prompt → purple (warning was shown), Pass → magenta (silent record).
const ACTION_TAG_STYLE: Record<string, { bg: string; color: string }> = {
  Prompt: { bg: 'rgba(107, 35, 202, 1)', color: '#E1E1E1' },
  Pass: { bg: 'rgba(163, 7, 85, 1)', color: '#FFFFFF' },
};

function formatList(value: unknown): string[] {
  if (value === null || value === undefined || value === '') return [];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  return [String(value)];
}

const TIMELINE_DOT_COLOR = '#D3D8DA';
const TIMELINE_LINE_COLOR = 'rgba(255, 255, 255, 0.12)';
const ALERT_BG = 'rgba(253, 215, 111, 0.08)';
const ALERT_BORDER = '#FDD76F';

interface TimelineEvent {
  title: string;
  actionTag?: string;
  timestamp: string;
  reason?: string;
}

const TimelineEntry = ({
  event,
  isLast,
  primaryColor,
  secondaryColor,
}: {
  event: TimelineEvent;
  isLast: boolean;
  primaryColor: string;
  secondaryColor: string;
}) => {
  const { t } = useTranslation();
  const { title, actionTag, timestamp, reason } = event;
  const tagStyle = actionTag ? ACTION_TAG_STYLE[actionTag] : undefined;
  return (
    <Flex gap="16px" alignSelf="stretch">
      {/* Left rail: dot + vertical divider */}
      <Flex direction="column" align="center" pt="8px" flexShrink={0}>
        <Box
          width="9px"
          height="9px"
          borderRadius="50%"
          backgroundColor={TIMELINE_DOT_COLOR}
        />
        {!isLast && (
          <Box
            flex="1"
            width="1px"
            backgroundColor={TIMELINE_LINE_COLOR}
            mt="2px"
          />
        )}
      </Flex>
      {/* Right content */}
      <Box flex="1" pb={isLast ? '0' : '24px'}>
        <Flex align="center" gap="8px" mb="8px">
          <Text fontWeight="600" fontSize="16px" color={primaryColor}>
            {title}
          </Text>
          {actionTag && tagStyle ? (
            <Tag
              backgroundColor={tagStyle.bg}
              color={tagStyle.color}
              borderRadius="9999px"
              px="8px"
            >
              {t(`quarantine.action.${actionTag}`, {
                defaultValue: actionTag,
              })}
            </Tag>
          ) : null}
        </Flex>
        <Text color={secondaryColor} mb={reason ? '8px' : '0'}>
          {timestamp}
        </Text>
        {reason ? (
          <Flex
            gap="8px"
            p="8px 8px 8px 16px"
            backgroundColor={ALERT_BG}
            border="1px solid"
            borderColor={ALERT_BORDER}
            borderRadius="4px"
            align="flex-start"
          >
            <Icon
              as={WarningTriangleIcon}
              color={ALERT_BORDER}
              fontSize="20px"
              mt="2px"
              flexShrink={0}
            />
            <Text color={primaryColor} flex="1">
              {reason}
            </Text>
          </Flex>
        ) : null}
      </Box>
    </Flex>
  );
};

export const MisdirectedDetailDrawer = () => {
  const { t } = useTranslation();
  const open = useMisdirectedDetailStore((s) => s.open);
  const sessionId = useMisdirectedDetailStore((s) => s.sessionId);
  const closeDrawer = useMisdirectedDetailStore((s) => s.closeDrawer);
  const {
    state: { timeRange },
  } = useLogReducerContext();
  const { offset } = useUtcOffsetStore();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  const [loading, setLoading] = useState(false);
  const [rounds, setRounds] = useState<MisdirectedRound[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !sessionId) return;
    let aborted = false;
    setLoading(true);
    setError(null);
    setRounds([]);
    LogApi.getRawLog({
      type: 'detection',
      timeRange,
      page: { start: 1, size: 100 },
      sort: { field: 'timestamp', direction: 'desc' },
      searches: JSON.stringify([
        {
          field: 'tm_md_session_id',
          values: [sessionId],
          type: 'terms',
        },
      ]),
    })
      .then((res) => {
        if (aborted) return;
        const list: MisdirectedRound[] = res.data?.contentList ?? [];
        setRounds(list);
      })
      .catch((e) => {
        if (aborted) return;
        // eslint-disable-next-line no-console
        console.error('[MisdirectedDetail] failed to load rounds', e);
        setError(t('log.misdirectedDetail.loadFailed') as string);
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, [open, sessionId, timeRange, t]);

  const original = useMemo(
    () => rounds.find((r) => Number(r.mdDetectRound) === 0),
    [rounds]
  );
  const history = useMemo(
    () =>
      rounds
        .filter((r) => Number(r.mdDetectRound) === -1)
        .sort((a, b) =>
          (a.msgSubmitTime ?? '').localeCompare(b.msgSubmitTime ?? '')
        ),
    [rounds]
  );

  // round=0 is the single source of terminal status under the current contract;
  // round=-1 rows are scanner re-detections only and their mdDetectStatus is
  // ignored. Empty/missing status falls back to OnHold (orange) badge.
  const latestStatus = original?.mdDetectStatus || 'OnHold';

  const finalActionLabel = t(`quarantine.mdDetectStatus.${latestStatus}`, {
    defaultValue: latestStatus,
  }) as string;
  // Unknown status falls back to the OnHold (orange) color so we don't
  // mislead the user with a "send-anyway red" badge for an unmapped state.
  const finalActionColor =
    FINAL_ACTION_COLOR[latestStatus] ?? FINAL_ACTION_COLOR.OnHold;

  const formatTime = useCallback(
    (iso?: string): string => {
      if (!iso) return '';
      return dayjs
        .utc(iso)
        .utcOffset(offset)
        .format(t('common.momentDateFormat') as string);
    },
    [offset, t]
  );

  const renderFieldRow = (label: string, values: string[]) => (
    <Flex align="flex-start" mb="12px">
      <Box width="180px" flexShrink={0} pt="6px">
        <Text color={colorStyle.color.secondary} fontWeight="500">
          {label}
        </Text>
      </Box>
      <Box flex="1" wordBreak="break-all">
        {values.length === 0 ? (
          <Text color={colorStyle.color.primary} py="6px">
            —
          </Text>
        ) : (
          values.map((v, i) => (
            <Text key={i} color={colorStyle.color.primary} py="6px">
              {v}
            </Text>
          ))
        )}
      </Box>
    </Flex>
  );

  // Timeline assembly contract (see openspec quarantine-log-misdirected-ui):
  //   round=0 always produces Initial detection; if its mdDetectStatus is a
  //   terminal status, the same round=0 row also produces the corresponding
  //   terminal user-action event (timestamp = mdSentTime). round=-1 rows are
  //   scanner re-detections only — their mdDetectStatus is ignored here.
  //   Order: Initial → Re-triggered (asc by msgSubmitTime) → terminal (if any).
  const timelineEvents = useMemo<TimelineEvent[]>(() => {
    const reasonFor = (r: MisdirectedRound): string | undefined => {
      if (r.action !== 'Prompt') return undefined;
      return (
        renderDetectionReason(t, r.mdDetectId, r.mdDetectParameters) ||
        undefined
      );
    };
    const detectionTime = (r: MisdirectedRound): string =>
      formatTime(r.msgSubmitTime || r.msgDeliveryTime);
    const events: TimelineEvent[] = [];
    if (original) {
      events.push({
        title: t('log.misdirectedDetail.event.initialDetection') as string,
        actionTag: original.action || undefined,
        timestamp: detectionTime(original),
        reason: reasonFor(original),
      });
      // Pass + Send Anyway: the email passed the misdirected scan (silent
      // record) yet the user sent anyway. Collapse to the single Pass entry
      // and drop the redundant terminal "Send Anyway" event. Prompt-action
      // rows keep the full timeline below.
      if (
        original.action === 'Pass' &&
        original.mdDetectStatus === 'SendAnyway'
      ) {
        return events;
      }
    }
    history.forEach((r) => {
      events.push({
        title: t('log.misdirectedDetail.event.retriggered') as string,
        actionTag: r.action || undefined,
        timestamp: detectionTime(r),
        reason: reasonFor(r),
      });
    });
    if (original?.mdDetectStatus === 'SendAnyway') {
      events.push({
        title: t('log.misdirectedDetail.event.sendAnyway') as string,
        timestamp: formatTime(original.mdSentTime),
      });
    } else if (original?.mdDetectStatus === 'ActionTaken') {
      events.push({
        title: t('log.misdirectedDetail.event.actionTaken') as string,
        timestamp: formatTime(original.mdSentTime),
      });
    }
    return events;
  }, [original, history, formatTime, t]);

  return (
    <Drawer isOpen={open} onClose={closeDrawer} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent maxWidth="832px" width="832px">
        <DrawerHeader>
          <Flex align="center" justify="space-between">
            <Text
              fontWeight="600"
              fontSize="20px"
              color={colorStyle.color.primary}
            >
              {t('log.misdirectedDetail.title')}
            </Text>
            <DrawerCloseButton onClick={closeDrawer} />
          </Flex>
        </DrawerHeader>
        <DrawerBody p="12px 24px 24px">
          {loading ? (
            <Flex justify="center" align="center" minHeight="200px">
              <Spinner />
            </Flex>
          ) : error ? (
            <Text color={colorStyle.color.error}>{error}</Text>
          ) : !rounds.length ? (
            <Text color={colorStyle.color.secondary}>
              {t('log.misdirectedDetail.empty')}
            </Text>
          ) : (
            <Stack spacing="24px">
              {/* Final action header card */}
              <Box backgroundColor="#1B1B1B" p="16px" borderRadius="4px">
                <Text color={colorStyle.color.secondary} mb="4px">
                  {t('log.misdirectedDetail.finalAction')}
                </Text>
                <Text fontWeight="600" fontSize="20px" color={finalActionColor}>
                  {finalActionLabel}
                </Text>
              </Box>

              {/* Original email profile */}
              <Box>
                <Text
                  fontWeight="600"
                  fontSize="16px"
                  color={colorStyle.color.primary}
                  mb="12px"
                >
                  {t('log.misdirectedDetail.originalProfile')}
                </Text>
                {renderFieldRow(
                  t('log.misdirectedDetail.field.subject'),
                  formatList(original?.msgSubject)
                )}
                {renderFieldRow(
                  t('log.misdirectedDetail.field.sender'),
                  formatList(original?.msgFrom)
                )}
                {renderFieldRow(
                  t('log.misdirectedDetail.field.recipient'),
                  formatList(original?.msgTo)
                )}
                {renderFieldRow(
                  t('log.misdirectedDetail.field.sentAt'),
                  original?.msgSubmitTime || original?.msgDeliveryTime
                    ? [
                        formatTime(
                          original.msgSubmitTime || original.msgDeliveryTime
                        ),
                      ]
                    : []
                )}
                {renderFieldRow(
                  t('log.misdirectedDetail.field.attachment'),
                  formatList(original?.mdFileName)
                )}
              </Box>

              {/* Detection history timeline */}
              <Box>
                <Text
                  fontWeight="600"
                  fontSize="16px"
                  color={colorStyle.color.primary}
                  mb="12px"
                >
                  {t('log.misdirectedDetail.detectionHistory')}
                </Text>
                <Box py="8px">
                  {timelineEvents.map((evt, idx) => (
                    <TimelineEntry
                      key={`${evt.title}-${idx}`}
                      event={evt}
                      isLast={idx === timelineEvents.length - 1}
                      primaryColor={colorStyle.color.primary}
                      secondaryColor={colorStyle.color.secondary}
                    />
                  ))}
                </Box>
              </Box>
            </Stack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
